<template>
  <div class="intg">
    <!-- ── Hero ── -->
    <div class="intg-hero">
      <div class="intg-hero__fx">
        <div class="intg-hero__orb intg-hero__orb--1"/>
        <div class="intg-hero__orb intg-hero__orb--2"/>
        <div class="intg-hero__grid"/>
      </div>
      <div class="intg-hero__content">
        <div class="intg-hero__icon"><i class="fa-solid fa-plug-circle-check"/></div>
        <div class="intg-hero__text">
          <h4 class="intg-hero__title">{{ $t('integrationsPage.title') }}</h4>
          <p class="intg-hero__sub">{{ $t('integrationsPage.subtitle') }}</p>
        </div>
        <div class="intg-hero__badge">
          <b class="tabular">{{ connectedCount }}</b> {{ $t('integrationsPage.connected') }}
        </div>
      </div>
    </div>

    <!-- ── Category sections ── -->
    <section v-for="(group, groupIndex) in groups" :key="group.key" class="intg-shell">
      <div class="intg-section">
        <span class="intg-section__icon" :style="{'--tint': group.tint, '--tint-soft': group.tintSoft}">
          <i :class="group.icon"/>
        </span>
        <span class="intg-section__title">{{ group.title }}</span>
        <span class="intg-section__line"/>
        <span class="intg-section__num">{{ String(groupIndex + 1).padStart(2, '0') }}</span>
      </div>
      <p v-if="group.hint" class="intg-section__hint">{{ group.hint }}</p>

      <div class="intg-grid">
        <div v-for="item in group.items" :key="item.name" class="intg-card">
          <div class="intg-card__tile" :style="{background: item.color}">{{ item.monogram }}</div>
          <div class="intg-card__mid">
            <div class="intg-card__name">{{ item.name }}</div>
            <div class="intg-card__desc">{{ item.description }}</div>
          </div>
          <span class="intg-status" :class="item.available ? 'intg-status--available' : 'intg-status--connected'">
            <i :class="item.available ? 'fa-regular fa-circle' : 'fa-solid fa-circle-check'" class="mr-25"/>
            {{ item.available ? $t('integrationsPage.available') : $t('integrationsPage.connected') }}
          </span>
        </div>
      </div>

      <!-- booking platforms ride along under the channel managers -->
      <template v-if="group.key === 'channels'">
        <div class="intg-platforms">
          <span class="intg-platforms__label">{{ $t('integrationsPage.platformsHint') }}</span>
          <span v-for="platform in platforms" :key="platform" class="intg-platform">
            <platform-logo :platform="platform" size="16px"/>
            <span class="intg-platform__name">{{ platform }}</span>
          </span>
        </div>
      </template>
    </section>
  </div>
</template>

<script>
import PlatformLogo from '@/components/widgets/PlatformLogo.vue'

export default {
  name: 'IntegrationsPage',
  components: {PlatformLogo},
  data: () => ({
    platforms: ['Airbnb', 'Booking', 'Expedia', 'Vrbo', 'Agoda'],
  }),
  computed: {
    groups() {
      const $t = key => this.$t(`integrationsPage.${key}`)
      return [
        {
          key: 'channels', icon: 'fa-solid fa-arrows-rotate', tint: '#7367f0', tintSoft: 'rgba(115,103,240,.12)',
          title: $t('channelManagers'), hint: $t('channelManagersHint'),
          items: [
            {name: 'Hostaway', monogram: 'H', color: 'linear-gradient(135deg,#12a5a5,#0c7f80)', description: $t('descHostaway')},
            {name: 'Apaleo', monogram: 'A', color: 'linear-gradient(135deg,#ff8a3d,#e05b16)', description: $t('descApaleo')},
            {name: 'Lodgify', monogram: 'L', color: 'linear-gradient(135deg,#f65446,#c93325)', description: $t('descLodgify')},
            {name: 'Cloudbeds', monogram: 'C', color: 'linear-gradient(135deg,#2f7cf6,#1a56c4)', description: $t('descCloudbeds')},
            {name: 'SiteMinder', monogram: 'S', color: 'linear-gradient(135deg,#e33a52,#b01f35)', description: $t('descSiteminder')},
            {name: 'Rentals United', monogram: 'R', color: 'linear-gradient(135deg,#3f6ce0,#2647a8)', description: $t('descRentalUnited')},
          ],
        },
        {
          key: 'payments', icon: 'fa-solid fa-credit-card', tint: '#28c76f', tintSoft: 'rgba(40,199,111,.12)',
          title: $t('payments'), hint: null,
          items: [
            {name: 'Stripe Payments', monogram: 'S', color: 'linear-gradient(135deg,#7a73ff,#635bff)', description: $t('descStripePayments')},
            {name: 'Stripe Identity', monogram: 'ID', color: 'linear-gradient(135deg,#9b94ff,#7a73ff)', description: $t('descStripeIdentity')},
          ],
        },
        {
          key: 'communication', icon: 'fa-solid fa-tower-broadcast', tint: '#00cfe8', tintSoft: 'rgba(0,207,232,.12)',
          title: $t('communication'), hint: null,
          items: [
            {name: 'Twilio Voice', monogram: 'T', color: 'linear-gradient(135deg,#f5484d,#c4232a)', description: $t('descTwilioVoice')},
            {name: 'Twilio SMS', monogram: 'T', color: 'linear-gradient(135deg,#ff6f74,#f5484d)', description: $t('descTwilioSms')},
            {name: 'WhatsApp Business', monogram: 'W', color: 'linear-gradient(135deg,#3fdc7c,#1faf58)', description: $t('descWhatsapp')},
            {name: 'E-Mail / SMTP', monogram: '@', color: 'linear-gradient(135deg,#64748b,#3f4a5e)', description: $t('descEmail')},
          ],
        },
        {
          key: 'access', icon: 'fa-solid fa-lock-open', tint: '#ff9f43', tintSoft: 'rgba(255,159,67,.14)',
          title: $t('access'), hint: null,
          items: [
            {name: 'FLEXIPASS', monogram: 'F', color: 'linear-gradient(135deg,#26b6ff,#0d8fd4)', description: $t('descFlexipass')},
            {name: 'Keybox', monogram: 'K', color: 'linear-gradient(135deg,#c2aa8e,#a9863f)', description: $t('descKeybox')},
          ],
        },
        {
          key: 'tools', icon: 'fa-solid fa-wand-magic-sparkles', tint: '#9c6bfa', tintSoft: 'rgba(156,107,250,.12)',
          title: $t('tools'), hint: null,
          items: [
            {name: 'Typeform', monogram: 'T', color: 'linear-gradient(135deg,#3d3d3d,#191919)', description: $t('descTypeform')},
            {name: 'PriceLabs', monogram: 'P', color: 'linear-gradient(135deg,#5f74f6,#3a4fd0)', description: $t('descPricelabs'), available: true},
          ],
        },
      ]
    },
    connectedCount() {
      return this.groups.reduce((sum, group) => sum + group.items.filter(item => !item.available).length, 0)
    },
  },
}
</script>

<style lang="scss" scoped>
.tabular { font-variant-numeric: tabular-nums; }

/* ── canvas ── */
.intg {
  position: relative;
  &::before {
    content: ''; position: absolute; inset: -16px -16px 0; pointer-events: none;
    background:
      radial-gradient(560px 300px at 8% 0%, rgba(115,103,240,.07), transparent 70%),
      radial-gradient(520px 280px at 92% 10%, rgba(194,170,142,.10), transparent 70%);
  }
  > * { position: relative; }
}

/* ── hero (same family as the Analytics hub pulse) ── */
.intg-hero {
  position: relative; border-radius: 20px; margin-bottom: 20px;
  background: linear-gradient(125deg, #10172c 0%, #1c2b4f 52%, #4d4030 130%);
  box-shadow: 0 24px 60px -24px rgba(10,14,35,.55);
}
.intg-hero__fx { position: absolute; inset: 0; overflow: hidden; border-radius: inherit; pointer-events: none; }
.intg-hero__orb { position: absolute; border-radius: 50%; filter: blur(46px); opacity: .5; }
.intg-hero__orb--1 { width: 240px; height: 240px; top: -100px; right: -40px; background: radial-gradient(circle, rgba(115,103,240,.8), transparent 70%); }
.intg-hero__orb--2 { width: 180px; height: 180px; bottom: -90px; left: 24%; background: radial-gradient(circle, rgba(0,207,232,.55), transparent 70%); }
.intg-hero__grid {
  position: absolute; inset: 0; opacity: .22;
  background-image: radial-gradient(rgba(255,255,255,.4) 1px, transparent 1px);
  background-size: 24px 24px;
  -webkit-mask-image: linear-gradient(115deg, transparent 10%, #000 45%, transparent 90%);
  mask-image: linear-gradient(115deg, transparent 10%, #000 45%, transparent 90%);
}
.intg-hero__content { position: relative; display: flex; align-items: center; gap: 15px; padding: 22px 26px; }
.intg-hero__icon {
  width: 48px; height: 48px; border-radius: 15px; display: flex; align-items: center; justify-content: center;
  background: rgba(255,255,255,.14); backdrop-filter: blur(8px); font-size: 1.15rem; color: #eddcb8;
  border: 1px solid rgba(255,255,255,.18);
}
.intg-hero__text { flex: 1; min-width: 0; }
.intg-hero__title { font-weight: 800; font-size: 1.2rem; margin: 0; color: #fff; }
.intg-hero__sub { font-size: .8rem; color: rgba(255,255,255,.65); margin: 0; }
.intg-hero__badge {
  font-size: .8rem; font-weight: 600; color: #7fe0ae; white-space: nowrap;
  padding: 7px 14px; border-radius: 999px; background: rgba(40,199,111,.14); border: 1px solid rgba(40,199,111,.4);
  b { font-size: .95rem; }
}

/* ── shells & section headers ── */
.intg-shell {
  background: rgba(255,255,255,.55);
  border: 1px solid rgba(15,23,42,.05);
  border-radius: 22px;
  padding: 18px 20px 20px;
  margin-bottom: 18px;
  backdrop-filter: blur(10px);
}
.intg-section { display: flex; align-items: center; gap: 10px; margin-bottom: 6px; }
.intg-section__icon {
  width: 30px; height: 30px; border-radius: 9px; display: inline-flex; align-items: center; justify-content: center;
  background: var(--tint-soft); color: var(--tint); font-size: .8rem;
}
.intg-section__title { font-size: .82rem; font-weight: 800; text-transform: uppercase; letter-spacing: .07em; color: #475569; white-space: nowrap; }
.intg-section__line { flex: 1; height: 1px; background: linear-gradient(90deg, rgba(100,116,139,.22), transparent); }
.intg-section__num { font-size: 1.35rem; font-weight: 800; letter-spacing: -.02em; color: rgba(100,116,139,.22); line-height: 1; user-select: none; }
.intg-section__hint { font-size: .78rem; color: #94a3b8; margin: 0 0 12px 40px; }

/* ── cards ── */
.intg-grid { display: grid; gap: 14px; grid-template-columns: repeat(3, 1fr); margin-top: 10px; }
@media (max-width: 1300px) { .intg-grid { grid-template-columns: repeat(2, 1fr); } }
@media (max-width: 640px) { .intg-grid { grid-template-columns: 1fr; } }

.intg-card {
  display: flex; align-items: center; gap: 13px;
  background: #fff; border: 1px solid rgba(15,23,42,.06); border-radius: 15px;
  padding: 14px 16px;
  box-shadow: 0 2px 10px rgba(15,23,42,.04);
  transition: transform .18s ease, box-shadow .18s ease, border-color .18s ease;
  &:hover { transform: translateY(-2px); border-color: rgba(115,103,240,.4); box-shadow: 0 12px 26px -14px rgba(15,23,42,.25); }
}
.intg-card__tile {
  width: 44px; height: 44px; border-radius: 13px; flex-shrink: 0;
  display: inline-flex; align-items: center; justify-content: center;
  color: #fff; font-weight: 800; font-size: 1.05rem; letter-spacing: -.02em;
  box-shadow: 0 8px 18px -8px rgba(15,23,42,.4);
}
.intg-card__mid { flex: 1; min-width: 0; }
.intg-card__name { font-size: .9rem; font-weight: 800; color: #1f2a44; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.intg-card__desc { font-size: .74rem; color: #94a3b8; line-height: 1.45; margin-top: 2px; }
.intg-status {
  display: inline-flex; align-items: center; flex-shrink: 0; align-self: flex-start;
  font-size: .66rem; font-weight: 800; letter-spacing: .03em; text-transform: uppercase;
  padding: 4px 10px; border-radius: 999px; white-space: nowrap;
  &--connected { background: rgba(40,199,111,.13); color: #1f9d57; }
  &--available { background: rgba(100,116,139,.12); color: #64748b; }
}

/* ── platform chips ── */
.intg-platforms {
  display: flex; align-items: center; flex-wrap: wrap; gap: 10px;
  margin-top: 16px; padding-top: 14px; border-top: 1px dashed rgba(15,23,42,.08);
}
.intg-platforms__label { font-size: .72rem; font-weight: 700; text-transform: uppercase; letter-spacing: .05em; color: #94a3b8; margin-right: 4px; }
.intg-platform {
  display: inline-flex; align-items: center; gap: 7px;
  padding: 6px 12px; border-radius: 999px;
  background: #fff; border: 1px solid rgba(15,23,42,.07);
  box-shadow: 0 1px 4px rgba(15,23,42,.05);
}
.intg-platform__name { font-size: .76rem; font-weight: 700; color: #475569; }
</style>

<style lang="scss">
.dark-layout {
  .intg-shell { background: rgba(40,48,70,.45); border-color: rgba(255,255,255,.05); }
  .intg-section__title { color: #b4b7bd; }
  .intg-section__line { background: linear-gradient(90deg, rgba(255,255,255,.14), transparent); }
  .intg-section__num { color: rgba(255,255,255,.12); }
  .intg-card { background: #283046; border-color: rgba(255,255,255,.07); box-shadow: 0 2px 10px rgba(0,0,0,.2); }
  .intg-card__name { color: #e9ecf2; }
  .intg-platform { background: #283046; border-color: rgba(255,255,255,.08); }
  .intg-platform__name { color: #b4b7bd; }
  .intg-platforms { border-color: rgba(255,255,255,.1); }
}
</style>
